/**
 * Point d'entrée de l'app web Nestor.
 *
 * Phase 1, incrément 2 : monte l'AppShell sur `#app`. Le shell se
 * redessine automatiquement sur changement de route (router) et de
 * langue (i18n).
 */
import { mountAppShell } from './layouts/AppShell.js';
import { getLang, setLang, SUPPORTED_LANGS, t } from './i18n/index.js';
import { currentRoute, navigate } from './lib/router.js';
import { registerServiceWorker } from './lib/sw-register.js';
import { mountUpdateBanner } from './ui/UpdateBanner.js';
import { mountToastHost } from './ui/Toast.js';
import { mountDialogHost } from './ui/Dialog.js';
import { initSession, subscribeSession } from './stores/session.js';
import { syncHomesWithServer } from './stores/homes-sync.js';
import { installMutationsReplay } from './api/mutationsReplay.js';
import { installLocalIdRemapBroker } from './api/localIdRemap.js';
import { hydratePendingCount } from './stores/mutationsQueueStore.js';
import { initMonitoring } from './lib/monitoring.js';
import { installCrashGuard } from './lib/crashGuard.js';
import { initWeather } from './modules/home/weather.js';
import { onAppUrlOpen, onBackButton } from './lib/capacitor/app.js';
import { resolveDeepLink } from './lib/deeplink.js';
import { getModal, closeModal } from './stores/ui.js';

const root = document.getElementById('app');
if (!root) {
  throw new Error('Élément #app introuvable dans index.html');
}

// Widget météo (accueil) : hydrate depuis le cache localStorage AVANT
// le premier rendu (affichage instantané, hors-ligne compris). Sans
// cache, reste inactif jusqu'à ce que l'utilisateur active la météo.
initWeather();

const unmount = mountAppShell(root);

// Phase 11.2 : monitoring erreurs (Sentry). No-op sans VITE_SENTRY_DSN ;
// le SDK est lazy-loadé (chunk séparé) pour ne pas alourdir la bundle
// principale. Fire-and-forget — ne bloque jamais le boot.
void initMonitoring();

// Polish alpha (T3) : bandeau bienveillant sur crash non géré —
// jamais d'écran figé muet. Complète Sentry (qui logge sans afficher).
const uninstallCrashGuard = installCrashGuard();

// Phase 7.2c : deep-links natifs. Quand l'app native est ouverte via un
// lien (`https://nestor.app/invite/<token>` ou custom scheme), on
// normalise vers la route hash interne et on laisse le router prendre
// le relais. No-op en web (onAppUrlOpen retourne un désabonnement inerte
// hors Capacitor natif).
const unsubscribeDeepLink = onAppUrlOpen((url) => {
  const hashRoute = resolveDeepLink(url);
  if (hashRoute) {
    window.location.hash = hashRoute;
  }
});

// Phase 7.3 : bouton retour matériel Android. Priorité à fermer une
// modale ouverte (geste « back » = annuler), sinon on remonte dans
// l'historique si possible. No-op en web/iOS (onBackButton inerte hors
// Android natif).
const unsubscribeBackButton = onBackButton((canGoBack) => {
  if (getModal() !== null) {
    closeModal();
    return;
  }
  if (canGoBack) {
    window.history.back();
  }
});

// Phase 3.1 : enregistre le Service Worker en production. En dev,
// Vite sert les modules ES non bundlés et le SW n'a rien d'utile à
// faire (les `/assets/` immutables n'existent qu'en build).
registerServiceWorker();

// Phase 3.2 : monte le bandeau « Mise à jour disponible » qui réagit
// à l'event `nestor:sw-update` dispatché par sw-register. Idempotent.
// Le container vit HORS de l'AppShell (sur `document.body`) pour
// survivre aux re-renders.
const unmountBanner = mountUpdateBanner();

// Phase 3.3.4 : monte le host des toasts (feedback discret pour les
// mutations offline). Idempotent, container hors AppShell.
const unmountToasts = mountToastHost();

// Polish alpha (U1) : host des dialogs applicatifs (confirm/prompt),
// remplace les window.confirm/prompt natifs. Monté hors AppShell.
const unmountDialogs = mountDialogHost();

// Phase 4.3 : tente de récupérer la session côté API au boot.
// Fire-and-forget — l'AppShell se mount immédiatement en mode
// `loading` puis bascule sur `anonymous` / `authenticated` quand
// `/auth/me` répond. Erreur réseau (API down) → `anonymous +
// offline`. CLAUDE.md §2.4 préservé : l'app reste utilisable sans
// compte.
void initSession();

// Phase 4.5 : à chaque transition `→ authenticated`, déclenche la
// sync foyers serveur ↔ local (ADR 0009). Couvre 3 cas :
//   - Boot avec cookie valide → initSession résout en authenticated.
//   - Login / register depuis Settings → store passe à authenticated.
// Logout ne déclenche rien (les foyers locaux UUID resteront jusqu'à
// la prochaine sync sous une autre identité). syncHomesWithServer
// est idempotent : appel multiple = pas de double round-trip.
let lastSyncedStatus: string | null = null;
const unsubscribeSync = subscribeSession((session) => {
  if (session.status === 'authenticated' && lastSyncedStatus !== 'authenticated') {
    lastSyncedStatus = 'authenticated';
    void syncHomesWithServer();
  } else if (session.status !== 'authenticated') {
    lastSyncedStatus = session.status;
  }
});

// Phase 3.3.1 : queue offline-first des mutations API. Au boot,
// drain la queue (mutations restées en attente d'une session
// précédente) ; écoute aussi les `online` events pour relancer
// quand le réseau revient. Le câblage des api/*.ts pour ENQUEUE
// (vs juste replay) viendra incrément par incrément en 3.3.2+.
const uninstallReplay = installMutationsReplay();

// Phase 3.3.4 : hydrate le store réactif `pendingCount` depuis IDB
// au boot. Fire-and-forget — le badge UI affichera 0 jusqu'à ce que
// la lecture IDB résolve (typiquement < 50 ms).
void hydratePendingCount();

// Phase 3.3.7 : broker `localId → serverId`. Écoute l'event
// `nestor:mutation-remap` émis par le replay engine après un POST
// réussi (online direct ou replay) et applique le mapping au store
// domain correspondant (events / tasks / messages / ...). Évite le
// doublon visuel transitoire au prochain GET refresh.
const uninstallRemap = installLocalIdRemapBroker();

if (import.meta.hot) {
  import.meta.hot.dispose(() => {
    unmountBanner();
    unmountToasts();
    unmountDialogs();
    unsubscribeSync();
    uninstallReplay();
    uninstallRemap();
    unsubscribeDeepLink();
    unsubscribeBackButton();
    uninstallCrashGuard();
  });
}

// HMR : nettoie les abonnements pour éviter les doublons à chaque hot reload.
if (import.meta.hot) {
  import.meta.hot.dispose(() => {
    unmount();
  });
}

// Aide au test manuel pendant la Phase 1 — tant qu'il n'y a pas de
// sélecteur de langue dans toutes les vues, on expose l'API i18n sur
// `window.nestor`. Disponible uniquement en mode dev.
if (import.meta.env.DEV) {
  Object.assign(globalThis, {
    nestor: {
      getLang,
      setLang,
      t,
      SUPPORTED_LANGS,
      navigate,
      currentRoute,
    },
  });
  console.info(
    '[nestor] Dev API exposée sur window.nestor — essaye :\n' +
      "  nestor.setLang('en'), nestor.navigate('/cp/'), nestor.t('home')",
  );
}
